<template>
  <div class="sector-map-canvas" :class="{ 'sector-map-canvas--pick-small': pickSmall }">
    <div ref="host" class="sector-map-canvas__svg" />
    <div class="sector-map-canvas__overlay">
      <div
        v-for="chip in chips"
        :key="chip.key"
        class="sector-map-canvas__box"
        :class="`sector-map-canvas__box--${chip.kind}`"
        :style="chip.style"
      >
        <span class="sector-map-canvas__chip" :style="{ borderColor: chip.color }">{{ chip.text }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
// Renders the sector map being linked, highlights the hovered and selected shapes, labels the
// linked ones and reports clicks on shapes that take them.
import { onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import type { SectorMapLinking } from '@/helpers/sectorMap/linking'

interface Chip {
  key: string
  kind: 'sector' | 'section' | 'problem'
  text: string
  color: string
  style: Record<string, string>
}

const { linking, selectedKeys, hoverKey = null, pickSmall = false, showNames = true } = defineProps<{
  linking: SectorMapLinking
  selectedKeys: string[]
  hoverKey?: string | null
  pickSmall?: boolean
  showNames?: boolean
}>()

const emit = defineEmits<{
  pick: [key: string, event: MouseEvent]
  hover: [key: string | null]
}>()

const host = useTemplateRef<HTMLDivElement>('host')
const chips = ref<Chip[]>([])

function onClick(event: MouseEvent) {
  const key = linking.keyAt(event.target)
  if (key) emit('pick', key, event)
}

function onOver(event: MouseEvent) {
  emit('hover', linking.keyAt(event.target))
}

function onLeave() {
  emit('hover', null)
}

function mount() {
  const svg = linking.svg.value
  if (!host.value) return

  host.value.replaceChildren(...(svg ? [ svg ] : []))
  if (svg) linking.measure()
}

function paint() {
  const svg = linking.svg.value
  if (!svg || !host.value) {
    chips.value = []
    return
  }

  svg.querySelectorAll('[data-link-hover], [data-link-selected]').forEach((e) => {
    e.removeAttribute('data-link-hover')
    e.removeAttribute('data-link-selected')
  })

  if (hoverKey) linking.element(hoverKey)?.setAttribute('data-link-hover', '')
  selectedKeys.forEach(key => linking.element(key)?.setAttribute('data-link-selected', ''))

  if (!showNames) {
    chips.value = []
    return
  }

  const origin = host.value.getBoundingClientRect()
  const next: Chip[] = []

  for (const shape of linking.shapes.value) {
    if (!shape.target && !(shape.problem && !shape.small)) continue

    const rect = linking.element(shape.key)?.getBoundingClientRect()
    if (!rect) continue

    next.push({
      key: shape.key,
      kind: shape.target?.kind ?? 'problem',
      text: shape.target?.name ?? '⚠',
      color: shape.target?.color ?? '#d32f2f',
      style: {
        left: `${rect.left - origin.left}px`,
        top: `${rect.top - origin.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
      },
    })
  }

  chips.value = next
}

let observer: ResizeObserver | null = null

onMounted(() => {
  host.value!.addEventListener('click', onClick)
  host.value!.addEventListener('mouseover', onOver)
  host.value!.addEventListener('mouseleave', onLeave)
  observer = new ResizeObserver(() => paint())
  observer.observe(host.value!)
  mount()
  paint()
})

onBeforeUnmount(() => {
  observer?.disconnect()
  host.value?.removeEventListener('click', onClick)
  host.value?.removeEventListener('mouseover', onOver)
  host.value?.removeEventListener('mouseleave', onLeave)
})

watch(() => linking.svg.value, () => mount(), { flush: 'post' })

watch(
  () => [linking.version.value, linking.shapes.value, selectedKeys, hoverKey, showNames],
  () => paint(),
  { flush: 'post' },
)
</script>

<style scoped>
.sector-map-canvas {
  position: relative;
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px;
}

.sector-map-canvas__svg {
  display: flex;
  justify-content: center;
}

.sector-map-canvas__svg :deep(svg) {
  width: 100%;
  height: auto;
  max-height: 72vh;
}

.sector-map-canvas__overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.sector-map-canvas__box {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sector-map-canvas__box--sector {
  border: 1px dashed rgba(0, 0, 0, 0.35);
  align-items: flex-start;
  justify-content: flex-start;
}

.sector-map-canvas__box--sector .sector-map-canvas__chip {
  transform: translate(-4px, -50%);
  font-weight: 700;
}

.sector-map-canvas__box--problem {
  border: 2px dashed #d32f2f;
}

.sector-map-canvas__chip {
  background: #fff;
  border: 2px solid;
  border-radius: 999px;
  padding: 1px 8px;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.sector-map-canvas :deep([data-link-key]) {
  cursor: pointer;
}

.sector-map-canvas :deep([data-link-pick="background"]),
.sector-map-canvas:not(.sector-map-canvas--pick-small) :deep([data-link-pick="small"]) {
  pointer-events: none;
}

.sector-map-canvas :deep([data-link-hover]),
.sector-map-canvas :deep([data-link-hover] *) {
  stroke: #42a5f5 !important;
  stroke-width: 3px !important;
  vector-effect: non-scaling-stroke;
}

.sector-map-canvas :deep([data-link-selected]),
.sector-map-canvas :deep([data-link-selected] *) {
  stroke: #0d47a1 !important;
  stroke-width: 5px !important;
  vector-effect: non-scaling-stroke;
}
</style>
